import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useEditor, EditorContent } from '@tiptap/react';
import { useTheme, alpha } from '@mui/material/styles';
import {
  Box,
  CircularProgress,
  Typography,
  Chip,
  Avatar,
  Tooltip,
} from '@mui/material';

// TipTap extensions
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import CharacterCount from '@tiptap/extension-character-count';
import TaskList from '@tiptap/extension-task-list';
import TaskItem from '@tiptap/extension-task-item';
import Highlight from '@tiptap/extension-highlight';
import TextStyle from '@tiptap/extension-text-style';

// Custom extensions
import CommentMark from './extensions/CommentMark';
import LawCitationMark from './extensions/LawCitationMark';
import SuggestionExtension, { StrikeWithoutSuggestion } from './extensions/SuggestionExtension';
import { TextSelectionBubble, hideTextSelectionBubbles } from './extensions/TextSelectionBubble';
import { TabKeyHandler } from './extensions/TabKeyHandler';
import { RichTable } from './extensions/CustomTable';
import { ResizableImage } from './extensions/CustomImage';
import { TextColor } from './extensions/CustomColor';
import { TypographyExtension } from './extensions/CustomFont';
import { RiskHighlight } from './extensions/CustomHighlight';
import { AiExtension, AiKeyboardShortcut } from './extensions/AiExtension';
import { PageBreakExtension } from './extensions/PageBreakExtension';
import { PaginationPlus } from './extensions/pagination';

// Hooks
import { useCollaboration } from './hooks/useCollaboration';
import { useAutoSave } from './hooks/useAutoSave';
import { useComments } from './hooks/useComments';
import { useSuggestions } from './hooks/useSuggestions';
import { useCardPositions } from './hooks/useCardPositions';
import { usePageSettings, toPaginationOptions } from './hooks/usePageSettings';

// Components
import CardsColumn from './components/CardsColumn';

import CollabStatus from './components/CollabStatus';
import EditorNotification from './components/EditorNotification';
import ToolbarPlugin from './plugins/ToolbarPlugin';

import './Editor.css';

// ── Helpers ────────────────────────────────────────────────────────────────────

const COLORS = [
  '#7C3AED', '#E11D48', '#D97706', '#0891B2', '#2563EB',
  '#059669', '#9333EA', '#DC2626', '#0D9488', '#4F46E5',
  '#B91C1C', '#1D4ED8', '#9D174D', '#047857', '#7E22CE',
  '#C2410C', '#0E7490', '#6D28D9', '#BE185D', '#1E40AF',
];
const getRandomColor = () => COLORS[Math.floor(Math.random() * COLORS.length)];

const DEFAULT_CONTENT = `
  <h1>Empty Contract</h1>
  <p>This is a collaborative contract document.</p>
  <p>Feel free to edit and collaborate in real-time!</p>
`;

// Injected once per app session — avoids creating a new style tag on every mount.
let cursorStylesInjected = false;
function injectCursorStyles() {
  if (cursorStylesInjected || document.getElementById('collaboration-cursor-styles')) {
    cursorStylesInjected = true;
    return;
  }
  const el = document.createElement('style');
  el.id = 'collaboration-cursor-styles';
  el.textContent = `
    .collaboration-cursor__caret {
      position: relative; border-left: 2px solid; border-right: none;
      margin-left: -1px; margin-right: -1px; pointer-events: none;
      word-break: normal; height: 1.2em; background-color: transparent;
      z-index: 50; animation: blink 1s infinite;
    }
    .collaboration-cursor__label {
      position: absolute; top: -1.4em; left: -1px;
      border-radius: 3px 3px 3px 0; padding: 0.1rem 0.3rem;
      font-size: 12px; font-weight: 500; line-height: normal;
      white-space: nowrap; color: white;
      user-select: none; pointer-events: none; z-index: 51;
    }
    .collaboration-cursor__selection {
      background-color: inherit; pointer-events: none;
      position: relative; z-index: 20;
    }
    @keyframes blink {
      0% { opacity: 1; } 50% { opacity: 0.3; } 100% { opacity: 1; }
    }
  `;
  document.head.appendChild(el);
  cursorStylesInjected = true;
}

// ── Component ──────────────────────────────────────────────────────────────────

const DocumentEditor = React.forwardRef((props, ref) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { contractId } = useParams();
  const loading = useSelector(state => state.contract.loading);
  const contract = useSelector(state => state.contract.contract);
  const currentUser = useSelector(state => state.auth.user.user);

  // ── RTL detection ─────────────────────────────────────────────────────────
  // Use the contract's persisted language. For legacy contracts that pre-date
  // language persistence, sniff the content for a meaningful share of Arabic
  // characters and treat it as Arabic.
  const isRTL = React.useMemo(() => {
    if (!contract) return false;
    if (contract.language === 'ar') return true;
    if (contract.language) return false;
    const html = contract.content || '';
    if (!html) return false;
    const text = html.replace(/<[^>]+>/g, '');
    const arabic = (text.match(/[؀-ۿ]/g) || []).length;
    const latin = (text.match(/[A-Za-z]/g) || []).length;
    return arabic > 20 && arabic >= latin;
  }, [contract]);

  // ── Determine if user is a reviewer (forced suggest mode) ─────────────────
  // External users are always forced into suggest mode regardless of their role
  const isReviewer = React.useMemo(() => {
    if (!contract || !currentUser) return false;
    if (currentUser.account_type === 'external') return true;
    const collab = (contract.collaborators || []).find(c => c.id === currentUser.id);
    return collab?.role === 'reviewer';
  }, [contract, currentUser]);

  // ── Local state ────────────────────────────────────────────────────────────
  const [editorReady, setEditorReady] = useState(false);
  const [editorInitialized, setEditorInitialized] = useState(false);
  const [contentLoading, setContentLoading] = useState(true);
  const [selectedImageNode, setSelectedImageNode] = useState(null);

  // ── Stable refs for extension callbacks ───────────────────────────────────
  // Called by useCollaboration's cleanup BEFORE socket.disconnect() — saves on navigate-away
  const saveBeforeLeaveRef = useRef(null);
  const aiButtonRef = useRef(null);
  const onCommentClickRef = useRef(null);
  const onSuggestClickRef = useRef(null);
  // Mirrors suggestionHook.suggestMode — read by TextSelectionBubble & SuggestionExtension
  const suggestModeRef = useRef(false);
  // Mirrors locked state — read by TextSelectionBubble to hide when contract is locked
  const lockedRef = useRef(false);
  // Mirrors isReviewer — read by TextSelectionBubble to show comment+suggest only (no AI)
  const isReviewerRef = useRef(false);
  // Active regulatory alert — read by TextSelectionBubble to show AI suggest button
  const activeAlertRef = useRef(null);
  const onAISuggestClickRef = useRef(null);
  const lastSavedContent = useRef('');

  // Stable user data (color chosen once per session)
  const [currentUserData] = useState(() => ({
    id: currentUser?.id,
    first_name: currentUser?.first_name,
    last_name: currentUser?.last_name,
    color: getRandomColor(),
  }));

  // ── Page settings ──────────────────────────────────────────────────────────
  const { pageSettings, setPageSettings, pageSettingsRef } =
    usePageSettings({ contract });

  // ── Inline header/footer editing (double-click → contentEditable) ──────
  const headerClickRef = useRef(null);
  const footerClickRef = useRef(null);

  const makeZoneEditable = (contentEl, type) => {
    if (!contentEl || contentEl.isContentEditable) return;

    // Find the left content div (user's custom text) and right div (page numbers)
    const prefix = type === 'header' ? 'rm-page-header' : 'rm-page-footer';
    const leftEl = contentEl.querySelector(`.${prefix}-left`);
    const rightEl = contentEl.querySelector(`.${prefix}-right`);
    if (!leftEl) return;

    // Lock heights to prevent any content shift
    const parentEl = contentEl.parentElement;
    if (parentEl) parentEl.style.height = parentEl.offsetHeight + 'px';
    contentEl.style.boxSizing = 'border-box';
    contentEl.style.height = contentEl.offsetHeight + 'px';

    // Hide page-number side during editing
    if (rightEl) rightEl.style.display = 'none';

    // Remove any page-number spans, show only clean text
    leftEl.querySelectorAll('.rm-page-number, .rm-page-number-plus').forEach((s) => s.remove());

    // Pre-fill with saved content (clean text, no HTML)
    const ps = pageSettingsRef.current;
    leftEl.textContent = ps?.[type]?.content || '';

    // Style — subtle dashed separator, inline editing
    leftEl.contentEditable = 'true';
    leftEl.style.outline = 'none';
    leftEl.style.cursor = 'text';
    leftEl.style.float = 'none';
    leftEl.style.width = '100%';
    leftEl.style.minHeight = '1.4em';
    leftEl.style.fontSize = '0.78rem';
    leftEl.style.color = isDarkMode ? '#ccc' : '#444';
    leftEl.style.padding = '2px 4px';
    contentEl.style.borderBottom = type === 'header' ? '1px dashed #4285f4' : 'none';
    contentEl.style.borderTop = type === 'footer' ? '1px dashed #4285f4' : 'none';
    contentEl.style.background = 'rgba(66,133,244,0.04)';
    leftEl.focus();

    // Place cursor at end
    const range = document.createRange();
    range.selectNodeContents(leftEl);
    range.collapse(false);
    const sel = window.getSelection();
    sel.removeAllRanges();
    sel.addRange(range);

    const cleanup = () => {
      leftEl.removeEventListener('blur', onBlur);
      leftEl.removeEventListener('keydown', onKeyDown);
      leftEl.contentEditable = 'false';
      // Reset all leftEl inline styles we added
      leftEl.style.float = '';
      leftEl.style.width = '';
      leftEl.style.cursor = '';
      leftEl.style.outline = '';
      leftEl.style.minHeight = '';
      leftEl.style.fontSize = '';
      leftEl.style.color = '';
      leftEl.style.padding = '';
      // Reset contentEl styles
      contentEl.style.borderBottom = '';
      contentEl.style.borderTop = '';
      contentEl.style.background = '';
      contentEl.style.boxSizing = '';
      contentEl.style.height = '';
      // Restore hidden elements and unlock parent height last
      if (rightEl) rightEl.style.display = '';
      if (parentEl) parentEl.style.height = '';
    };

    const save = () => {
      const text = leftEl.innerText.trim();
      const key = type;
      const newSettings = { ...ps, [key]: { ...ps[key], enabled: true, content: text } };
      setPageSettings(newSettings);
      if (socketRef.current && editor) {
        socketRef.current.emit('save', { content: editor.getHTML(), settings: { page: newSettings } });
      }
    };

    const onBlur = () => {
      save();
      cleanup();
    };

    const onKeyDown = (e) => {
      if (e.key === 'Enter') { e.preventDefault(); leftEl.blur(); return; }
      if (e.key === 'Escape') { cleanup(); return; }
      // Ctrl+A → select only the footer/header text, not the whole document
      if (e.key === 'a' && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        const r = document.createRange();
        r.selectNodeContents(leftEl);
        const s = window.getSelection();
        s.removeAllRanges();
        s.addRange(r);
      }
      // Stop all keys from reaching ProseMirror (Backspace, arrows, etc.)
      e.stopPropagation();
    };

    leftEl.addEventListener('blur', onBlur);
    leftEl.addEventListener('keydown', onKeyDown);
  };

  headerClickRef.current = ({ event }) => {
    const headerEl = event.currentTarget;
    const contentEl = headerEl.querySelector('.rm-page-header-content');
    makeZoneEditable(contentEl, 'header');
  };

  footerClickRef.current = ({ event }) => {
    const footerEl = event.currentTarget;
    const contentEl = footerEl.querySelector('.rm-page-footer-content');
    makeZoneEditable(contentEl, 'footer');
  };

  // ── Collaboration (socket + Yjs) ───────────────────────────────────────────
  const {
    status,
    activeUsers,
    socketRef,
    socketState,
    providerRef,
    collaborationExtensions,
  } = useCollaboration({ contractId, currentUser, currentUserData, saveBeforeLeaveRef });

  // ── TipTap editor ─────────────────────────────────────────────────────────
  const editor = useEditor(
    {
      extensions: [
        StarterKit.configure({
          history: false,
          bulletList: { HTMLAttributes: { class: 'bullet-list' } },
          orderedList: { HTMLAttributes: { class: 'ordered-list' } },
          listItem: { HTMLAttributes: { class: 'list-item' } },
          strike: false,
        }),
        StrikeWithoutSuggestion,
        Placeholder.configure({ placeholder: '' }),
        Underline,
        TextAlign.configure({
          types: ['heading', 'paragraph', 'orderedList', 'bulletList'],
          alignments: ['left', 'center', 'right', 'justify'],
        }),
        Highlight,
        TaskList,
        TaskItem,
        CharacterCount.configure({ limit: 100000 }),
        TextStyle,
        TabKeyHandler,
        RichTable,
        TextColor,
        TypographyExtension,
        ResizableImage,
        RiskHighlight,
        AiExtension.configure({
          onAiRequest: (text, prompt) => {
            console.log('AI request:', prompt, 'for text:', text);
          },
        }),
        AiKeyboardShortcut,
        PageBreakExtension,
        ...(pageSettings.enabled
          ? [PaginationPlus.configure({
              ...toPaginationOptions(pageSettingsRef.current),
              onHeaderClick: (info) => headerClickRef.current?.(info),
              onFooterClick: (info) => footerClickRef.current?.(info),
            })]
          : []),
        TextSelectionBubble.configure({
          aiButtonRef,
          suggestModeRef,
          lockedRef,
          isReviewerRef,
          activeAlertRef,
          onCommentClick: (data) => onCommentClickRef.current?.(data),
          onSuggestClick: (data) => onSuggestClickRef.current?.(data),
          onAISuggestClick: (data) => onAISuggestClickRef.current?.(data),
        }),
        CommentMark,
        LawCitationMark,
        SuggestionExtension.configure({
          suggestModeRef,
          isReviewerRef,
        }),
        ...collaborationExtensions,
      ],
      onCreate: () => setEditorReady(true),
      content: null,
      editable: false,
      editorProps: {
        attributes: { class: 'focus:outline-none' },
        transformPastedHTML(html) {
          // Strip dark inline color styles on paste so text inherits the
          // editor theme color (fixes invisible text in dark mode).
          const isColorDark = (colorStr) => {
            const s = colorStr.trim().toLowerCase();
            if (s === 'black') return true;
            const hexMatch = s.match(/^#([0-9a-f]{3,8})$/);
            if (hexMatch) {
              let hex = hexMatch[1];
              if (hex.length === 3) hex = hex[0]+hex[0]+hex[1]+hex[1]+hex[2]+hex[2];
              if (hex.length >= 6) {
                const r = parseInt(hex.slice(0,2), 16);
                const g = parseInt(hex.slice(2,4), 16);
                const b = parseInt(hex.slice(4,6), 16);
                return r <= 100 && g <= 100 && b <= 100;
              }
            }
            const rgbMatch = s.match(/^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/);
            if (rgbMatch) {
              return +rgbMatch[1] <= 50 && +rgbMatch[2] <= 50 && +rgbMatch[3] <= 50;
            }
            return false;
          };
          return html.replace(
            /(?<![-\w])color\s*:\s*([^;\"']+)/gi,
            (match, value) => isColorDark(value) ? '' : match,
          );
        },
      },
    },
    [collaborationExtensions],
  );

  // ── Comment & suggestion hooks ─────────────────────────────────────────────
  const commentHook = useComments({
    editor: editorReady ? editor : null,
    socket: socketState,
    contractId,
    currentUser,
  });

  const suggestionHook = useSuggestions({
    editor: editorReady ? editor : null,
    socket: socketState,
    contractId,
    currentUser,
  });

  // Combined reconciliation callback for toolbar undo/redo buttons
  const onAfterUndoRedo = useCallback(() => {
    console.log('[DocumentEditor] onAfterUndoRedo called, scheduling reconcile in 50ms');
    setTimeout(() => {
      console.log('[DocumentEditor] running reconciliation now');
      commentHook.reconcileAfterUndoRedo();
      suggestionHook.reconcileAfterUndoRedo();
    }, 50);
  }, [commentHook.reconcileAfterUndoRedo, suggestionHook.reconcileAfterUndoRedo]);

  // Force suggest mode for reviewers — they can only propose changes, not edit directly
  React.useEffect(() => {
    if (isReviewer && !suggestionHook.suggestMode) {
      suggestionHook.setSuggestMode(true);
    }
  }, [isReviewer, suggestionHook.suggestMode]);

  // Keep suggestModeRef in sync with hook state (read by extensions via ref)
  // For reviewers, always force true regardless of hook state
  suggestModeRef.current = isReviewer ? true : suggestionHook.suggestMode;
  // Keep lockedRef in sync with contract status (read by TextSelectionBubble)
  lockedRef.current = ['PENDING_SIGNATURE', 'SIGNED'].includes(contract?.status);
  // Keep isReviewerRef in sync (read by TextSelectionBubble to show comment+suggest only)
  isReviewerRef.current = isReviewer;

  // Wire callback refs — updated every render so extensions always call current functions
  onCommentClickRef.current = ({ from, to, text, selectionY }) => {
    const bodyEl = document.querySelector('.editor-body');
    const bodyScrollTop = bodyEl?.scrollTop || 0;
    const bodyRect = bodyEl?.getBoundingClientRect();
    const documentY = selectionY - (bodyRect?.top ?? 0) + bodyScrollTop;
    // Clear any pending suggestion mark before opening a comment card
    suggestionHook.setPendingSuggestion(null);
    commentHook.setPendingComment({ from, to, selectedText: text, selectionY: Math.max(0, documentY) });
  };

  onSuggestClickRef.current = ({ from, to, text, selectionY }) => {
    const bodyEl = document.querySelector('.editor-body');
    const bodyScrollTop = bodyEl?.scrollTop || 0;
    const bodyRect = bodyEl?.getBoundingClientRect();
    const documentY = selectionY - (bodyRect?.top ?? 0) + bodyScrollTop;
    // Clear any pending comment mark before opening a suggestion card
    commentHook.setPendingComment(null);
    suggestionHook.setPendingSuggestion({ from, to, originalText: text, selectionY: Math.max(0, documentY) });
  };

  // ── Card positions ─────────────────────────────────────────────────────────
  // useCardPositions needs the mark ID (tempId in doc), not permanentId.
  // We pass markIds for position lookup, then re-key results to permanentIds for CardsColumn.
  const commentPositionsRaw = useCardPositions({
    editor: editorReady ? editor : null,
    items: commentHook.comments.map(c => ({ id: commentHook.getMarkId(c.id) })),
    attrKey: 'commentId',
  });
  const commentPositions = React.useMemo(() => {
    const mapped = {};
    commentHook.comments.forEach(c => {
      const markId = commentHook.getMarkId(c.id);
      if (commentPositionsRaw[markId] !== undefined) {
        mapped[c.id] = commentPositionsRaw[markId];
      }
    });
    return mapped;
  }, [commentPositionsRaw, commentHook.comments]);

  const suggestionPositionsRaw = useCardPositions({
    editor: editorReady ? editor : null,
    items: suggestionHook.pendingSuggestions.map(s => ({ id: suggestionHook.getMarkId(s.id) })),
    attrKey: 'suggestionId',
  });
  const suggestionPositions = React.useMemo(() => {
    const mapped = {};
    suggestionHook.pendingSuggestions.forEach(s => {
      const markId = suggestionHook.getMarkId(s.id);
      if (suggestionPositionsRaw[markId] !== undefined) {
        mapped[s.id] = suggestionPositionsRaw[markId];
      }
    });
    return mapped;
  }, [suggestionPositionsRaw, suggestionHook.pendingSuggestions]);

  // Reserve the cards gutter only when something will actually render in it.
  // A comment or suggestion can live in state without a mark in the document
  // (e.g. a system AI comment whose contextText didn't match, or a Yjs sync
  // that hasn't arrived) — in those cases CardsColumn skips it, so checking
  // logical existence alone leaves the gutter open and empty.
  const hasCards =
    commentHook.pendingComment !== null ||
    suggestionHook.pendingSuggestion !== null ||
    commentHook.comments.some(c => !c.resolved && commentPositions[c.id] !== undefined) ||
    suggestionHook.pendingSuggestions.some(s => suggestionPositions[s.id] !== undefined);

  // ── Auto-save ──────────────────────────────────────────────────────────────
  const { hasUnsavedChanges, setHasUnsavedChanges, saveContract, notification, closeNotification } =
    useAutoSave({ editor, socketRef, contractId, t, lastSavedContent, pageSettingsRef });

  // Update PaginationPlus options when page settings change
  useEffect(() => {
    if (!editor || !editorReady || !pageSettings.enabled) return;
    const opts = toPaginationOptions(pageSettings);
    editor.chain()
      .updatePageSize({
        pageHeight: opts.pageHeight,
        pageWidth: opts.pageWidth,
        marginTop: opts.marginTop,
        marginBottom: opts.marginBottom,
        marginLeft: opts.marginLeft,
        marginRight: opts.marginRight,
      })
      .updateHeaderContent(opts.headerLeft, opts.headerRight)
      .updateFooterContent(opts.footerLeft, opts.footerRight)
      .run();
  }, [pageSettings]); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep saveBeforeLeaveRef fresh every render so useCollaboration's cleanup
  // (which runs BEFORE socket.disconnect) can save via the still-live socket.
  saveBeforeLeaveRef.current = (socket) => {
    if (hasUnsavedChanges && editor) {
      const payload = { content: editor.getHTML() };
      if (pageSettingsRef.current) payload.settings = { page: pageSettingsRef.current };
      socket.emit('save', payload);
    }
  };

  // ── Expose editor to parent via ref ───────────────────────────────────────
  React.useImperativeHandle(ref, () => ({
    getEditor: () => editor,
    setActiveAlert: (alertData) => {
      // alertData: { alert, canEdit } or null
      activeAlertRef.current = alertData;
      // Force the selection bubble to re-render with the new alert state
      if (window.textSelectionBubble) window.textSelectionBubble._renderReact();
    },
    setOnAISuggestClick: (fn) => { onAISuggestClickRef.current = fn; },
    // Programmatic AI suggestion creation (used by chat document_review flow):
    // routes through the regular addSuggestion path so the suggestion is
    // marked, persisted via socket, and visible to other collaborators.
    // Returns the temp markId of the created suggestion (string), or null on failure.
    addAISuggestion: ({ from, to, originalText, suggestedText }) => {
      if (!editor) return null;
      const docSize = editor.state.doc.content.size;
      const safeFrom = Math.max(0, Math.min(from, docSize));
      const safeTo = Math.max(safeFrom, Math.min(to, docSize));
      if (safeTo <= safeFrom) return null;
      try {
        // Snapshot the set of suggestion mark ids in the editor BEFORE
        // calling addSuggestion. We use the DOM diff afterwards to discover
        // the new tempId — this is more reliable than trusting the hook's
        // return value (which may be undefined on early returns or if a
        // hot-reload missed the file).
        const editorDom = editor.view?.dom;
        const before = new Set();
        if (editorDom) {
          editorDom.querySelectorAll('[data-sid]').forEach((el) => {
            const sid = el.getAttribute('data-sid');
            if (sid && sid !== 'pending') before.add(sid);
          });
        }
        const returned = suggestionHook.addSuggestion(suggestedText, {
          from: safeFrom,
          to: safeTo,
          originalText: originalText || '',
        });
        if (returned) return returned;
        // Fallback: diff the DOM to find the just-added mark.
        if (!editorDom) return null;
        const after = editorDom.querySelectorAll('[data-sid]');
        for (const el of after) {
          const sid = el.getAttribute('data-sid');
          if (sid && sid !== 'pending' && !before.has(sid)) {
            return sid;
          }
        }
        return null;
      } catch (e) {
        console.error('addAISuggestion failed:', e);
        return null;
      }
    },
    acceptAISuggestion: (suggestionId) => {
      if (!editor || !suggestionId) return false;
      try {
        suggestionHook.acceptSuggestion(suggestionId);
        return true;
      } catch (e) {
        console.error('acceptAISuggestion failed:', e);
        return false;
      }
    },
    rejectAISuggestion: (suggestionId) => {
      if (!editor || !suggestionId) return false;
      try {
        suggestionHook.rejectSuggestion(suggestionId);
        return true;
      } catch (e) {
        console.error('rejectAISuggestion failed:', e);
        return false;
      }
    },
    // Scroll the editor to the given suggestion mark and briefly flash it.
    // Lookup strategy:
    //   1. Try suggestionId directly (it may already be a mark id).
    //   2. Try the hook's getMarkId mapping (permanentId → tempId).
    //   3. Text fallback: if `originalText` is provided, find a <del> whose
    //      text starts with the same content. Survives a page reload where
    //      stored mark_ids no longer match the new permanent ids in the DOM.
    scrollToSuggestion: (suggestionId, originalText) => {
      if (!editor) return false;
      const editorDom = editor.view?.dom;
      if (!editorDom) return false;

      const findEl = () => {
        const candidates = [];
        if (suggestionId) {
          candidates.push(String(suggestionId));
          if (suggestionHook.getMarkId) {
            const mapped = suggestionHook.getMarkId(suggestionId);
            if (mapped) candidates.push(String(mapped));
          }
        }
        for (const sid of candidates) {
          const el = editorDom.querySelector(
            `[data-sid="${CSS.escape(sid)}"]`
          );
          if (el) return el;
        }
        // Text fallback — match against del elements' textContent
        if (!originalText) return null;
        const norm = (s) => (s || '')
          .replace(/\s+/g, ' ')
          .replace(/[‘’]/g, "'")
          .replace(/[“”]/g, '"')
          .replace(/[–—]/g, '-')
          .trim()
          .toLowerCase();
        const target = norm(originalText).slice(0, 40);
        if (!target) return null;
        const dels = editorDom.querySelectorAll('del[data-sid]');
        // Group del elements by data-sid (a single suggestion may span multiple <del> due to nested <strong> etc)
        const bySid = new Map();
        dels.forEach((d) => {
          const sid = d.getAttribute('data-sid');
          if (!sid) return;
          if (!bySid.has(sid)) bySid.set(sid, '');
          bySid.set(sid, bySid.get(sid) + d.textContent);
        });
        for (const [sid, txt] of bySid.entries()) {
          if (norm(txt).slice(0, 40) === target) {
            return editorDom.querySelector(`[data-sid="${CSS.escape(sid)}"]`);
          }
        }
        return null;
      };

      const el = findEl();
      if (!el) return false;
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      const prevOutline = el.style.outline;
      const prevTransition = el.style.transition;
      el.style.transition = 'outline 0.2s ease';
      el.style.outline = '2px solid #f59e0b';
      el.style.outlineOffset = '2px';
      setTimeout(() => {
        el.style.outline = prevOutline || '';
        el.style.transition = prevTransition || '';
      }, 1600);
      // Return the resolved sid so the caller can use it for accept/reject
      return el.getAttribute('data-sid') || true;
    },
    // Resolve the suggestion's current mark id by trying the stored id and
    // the text fallback. Used by Accept/Reject to recover after reload.
    resolveSuggestionMarkId: (suggestionId, originalText) => {
      if (!editor) return null;
      const editorDom = editor.view?.dom;
      if (!editorDom) return null;
      const candidates = [];
      if (suggestionId) {
        candidates.push(String(suggestionId));
        if (suggestionHook.getMarkId) {
          const mapped = suggestionHook.getMarkId(suggestionId);
          if (mapped) candidates.push(String(mapped));
        }
      }
      for (const sid of candidates) {
        if (editorDom.querySelector(`[data-sid="${CSS.escape(sid)}"]`)) return sid;
      }
      if (!originalText) return null;
      const norm = (s) => (s || '')
        .replace(/\s+/g, ' ')
        .replace(/[‘’]/g, "'")
        .replace(/[“”]/g, '"')
        .replace(/[–—]/g, '-')
        .trim()
        .toLowerCase();
      const target = norm(originalText).slice(0, 40);
      if (!target) return null;
      const bySid = new Map();
      editorDom.querySelectorAll('del[data-sid]').forEach((d) => {
        const sid = d.getAttribute('data-sid');
        if (!sid) return;
        bySid.set(sid, (bySid.get(sid) || '') + d.textContent);
      });
      for (const [sid, txt] of bySid.entries()) {
        if (norm(txt).slice(0, 40) === target) return sid;
      }
      return null;
    },
    addExternalSuggestion: (suggestion, from, to) => {
      // Add a backend-created suggestion to editor marks + hook state (no socket emit)
      if (!editor) return;
      editor.commands.addSuggestion({
        id: suggestion.id,
        from,
        to,
        suggestedText: suggestion.suggestedText,
      });
      // Add to hook state so the card renders
      suggestionHook.addExternalSuggestion?.(suggestion);
    },
    getUnresolvedStatus: () => ({
      unresolvedComments: commentHook.comments.filter(c => !c.resolved).length,
      pendingSuggestions: suggestionHook.pendingSuggestions.length,
    }),
    saveBeforeDownload: () => new Promise((resolve) => {
      if (!hasUnsavedChanges || !socketRef.current || !editor) {
        resolve();
        return;
      }
      const content = editor.getHTML();
      const payload = { content };
      if (pageSettingsRef?.current) payload.settings = { page: pageSettingsRef.current };
      socketRef.current.emit('save', payload);
      lastSavedContent.current = content;
      setHasUnsavedChanges(false);
      setTimeout(resolve, 500);
    }),
  }), [editor, commentHook.comments, suggestionHook.pendingSuggestions, hasUnsavedChanges, socketRef, pageSettingsRef, setHasUnsavedChanges]);

  // ── Inject collaboration cursor CSS ───────────────────────────────────────
  useEffect(() => {
    injectCursorStyles();
  }, []);

  // ── Initial content: load after Yjs provider syncs ────────────────────────
  useEffect(() => {
    if (!editor || !providerRef.current || !editorReady) return;

    const handleSync = (syncData) => {
      const isLocked = ['PENDING_SIGNATURE', 'SIGNED'].includes(contract?.status);
      const serverHadState = syncData?.serverHadState || false;

      // Only seed from database when the server had no Yjs state (first user
      // or server restart).  When another user is already editing, the Yjs
      // updates from the server will have populated the XmlFragment — calling
      // setContent here would overwrite their live changes.
      const ydoc = providerRef.current?.doc;
      const fragment = ydoc?.getXmlFragment?.('content');
      const yjsHasContent = fragment && fragment.length > 0;

      if (!yjsHasContent && !serverHadState) {
        const content = contract?.content?.trim() ? contract.content : DEFAULT_CONTENT;
        try {
          editor.commands.setContent(content);
        } catch {
          editor.commands.setContent(DEFAULT_CONTENT);
        }
      }

      editor.setEditable(!isLocked);
      setEditorInitialized(true);
      setContentLoading(false);
      lastSavedContent.current = editor.getHTML();
      setHasUnsavedChanges(false);
    };

    providerRef.current.on('synced', handleSync);
    return () => {
      providerRef.current?.off('synced', handleSync);
    };
  }, [editor, contract?.content, editorReady]); // eslint-disable-line react-hooks/exhaustive-deps

  // Lock / unlock editor when contract status changes (e.g. sent for signature)
  useEffect(() => {
    if (!editor || !editorInitialized) return;
    const isLocked = ['PENDING_SIGNATURE', 'SIGNED'].includes(contract?.status);
    editor.setEditable(!isLocked);
  }, [editor, contract?.status, editorInitialized]);

  // Apply restored content from history into the Yjs-backed editor
  const contentRestored = useSelector(state => state.contract.contentRestored);
  const restoredContent = useSelector(state => state.contract.restoredContent);
  useEffect(() => {
    if (!contentRestored || !restoredContent || !editor || !editorInitialized) return;
    editor.commands.setContent(restoredContent);
    lastSavedContent.current = editor.getHTML();
    setHasUnsavedChanges(false);
    // Persist the restored content via socket so Yjs state is updated
    if (socketRef.current) {
      const payload = { content: editor.getHTML() };
      if (pageSettingsRef.current) payload.settings = { page: pageSettingsRef.current };
      socketRef.current.emit('save', payload);
    }
  }, [contentRestored]); // eslint-disable-line react-hooks/exhaustive-deps

  // Dismiss contentLoading when editor gets content typed in (empty doc edge case)
  useEffect(() => {
    if (!editor) return;
    const handleUpdate = () => {
      if (contentLoading && !editor.isEmpty) setContentLoading(false);
    };
    editor.on('update', handleUpdate);
    return () => editor.off('update', handleUpdate);
  }, [editor, contentLoading]);

  // Broadcast local cursor position to other collaborators via awareness
  useEffect(() => {
    if (!editor || !providerRef.current?.awareness) return;

    const unsubscribe = editor.on('selectionUpdate', ({ editor: e }) => {
      const { from, to } = e.state.selection;
      const currentState = providerRef.current.awareness.getLocalState() || {};
      providerRef.current.awareness.setLocalState({
        ...currentState,
        user: currentState.user || {
          name: `${currentUserData.first_name || ''} ${currentUserData.last_name || ''}`.trim(),
          color: currentUserData.color,
          id: String(currentUser.id),
        },
        selection: { anchor: from, head: to },
      });
    });

    return () => {
      if (typeof unsubscribe === 'function') unsubscribe();
    };
  }, [editor, currentUser, currentUserData]); // eslint-disable-line react-hooks/exhaustive-deps

  // Click-to-focus: when the cursor lands inside a comment or suggestion mark,
  // activate the corresponding card so it snaps to align with its anchor text.
  useEffect(() => {
    if (!editor) return;

    const handleSelectionUpdate = ({ editor: e }) => {
      const { from } = e.state.selection;
      try {
        const resolved = e.state.doc.resolve(from);
        const marks = resolved.marks();

        const commentMark = marks.find(
          m => m.type.name === 'comment' && m.attrs.commentId && m.attrs.commentId !== 'pending'
        );
        const suggMark = marks.find(
          m => m.type.name === 'suggestionInsert' || m.type.name === 'suggestionDelete'
        );

        if (commentMark) {
          // Mark has tempId, resolve to permanentId for state matching
          commentHook.setActiveCommentId(commentHook.resolveMarkId(commentMark.attrs.commentId));
          suggestionHook.setActiveSuggestionId(null);
        } else if (suggMark && suggMark.attrs.suggestionId) {
          suggestionHook.setActiveSuggestionId(suggestionHook.resolveMarkId(suggMark.attrs.suggestionId));
          commentHook.setActiveCommentId(null);
        } else {
          // Clicked on unmarked text inside editor — collapse any open card
          commentHook.setActiveCommentId(null);
          suggestionHook.setActiveSuggestionId(null);
        }
      } catch {
        // Ignore resolve errors for out-of-range positions
      }
    };

    editor.on('selectionUpdate', handleSelectionUpdate);
    return () => editor.off('selectionUpdate', handleSelectionUpdate);
  }, [editor, commentHook, suggestionHook]); // eslint-disable-line react-hooks/exhaustive-deps

  // Suggest mode: auto-trigger card when user finishes selecting text.
  // Uses TipTap's selectionUpdate event (fires AFTER PM state is committed, no
  // re-entrancy risk) — identical behaviour to the normal-mode bubble click.
  useEffect(() => {
    if (!editor) return;
    let debounceTimer = null;

    const handleSelectionUpdate = () => {
      clearTimeout(debounceTimer);
      if (!suggestModeRef.current) return;
      // Reviewers pick comment or suggest from the bubble — skip auto-trigger
      if (isReviewerRef.current) return;
      const { from, to } = editor.state.selection;
      if (from === to) return;

      debounceTimer = setTimeout(() => {
        if (!suggestModeRef.current) return;
        const { from: curFrom, to: curTo } = editor.state.selection;
        if (curFrom === curTo) return;
        try {
          const startCoords = editor.view.coordsAtPos(curFrom);
          const endCoords = editor.view.coordsAtPos(curTo);
          const selectionY = (startCoords.top + endCoords.bottom) / 2;
          const text = editor.state.doc.textBetween(curFrom, curTo, ' ');
          console.log('[SuggestMode] debounce fired → from=%d to=%d text=%o', curFrom, curTo, text);
          onSuggestClickRef.current?.({ from: curFrom, to: curTo, text, selectionY });
        } catch (e) {
          // ignore coord errors
        }
      }, 300);
    };

    editor.on('selectionUpdate', handleSelectionUpdate);
    return () => {
      editor.off('selectionUpdate', handleSelectionUpdate);
      clearTimeout(debounceTimer);
    };
  }, [editor]); // eslint-disable-line react-hooks/exhaustive-deps

  // Track selected image node for image toolbar
  useEffect(() => {
    if (!editor) return;
    const handleSelectionUpdate = () => {
      if (!editor.isActive('resizableImage')) {
        setSelectedImageNode(null);
        return;
      }
      const { state } = editor;
      const { from } = state.selection;
      let pos = from;
      state.doc.nodesBetween(from, from, (node, nodePos) => {
        if (node.type.name === 'resizableImage') { pos = nodePos; return false; }
        return true;
      });
      setSelectedImageNode({ node: state.doc.nodeAt(pos), pos });
    };
    editor.on('selectionUpdate', handleSelectionUpdate);
    editor.on('focus', handleSelectionUpdate);
    return () => {
      editor.off('selectionUpdate', handleSelectionUpdate);
      editor.off('focus', handleSelectionUpdate);
    };
  }, [editor]);

  // Periodic awareness cleanup (stale cursors)
  useEffect(() => {
    if (!providerRef.current?.awareness) return;
    const interval = setInterval(() => {
      try {
        providerRef.current?.cleanupDuplicateAwarenessStates?.();
      } catch (err) {
        console.error('Awareness cleanup error:', err);
      }
    }, 10000);
    return () => clearInterval(interval);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Cmd/Ctrl+K → AI button
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!(e.metaKey || e.ctrlKey) || e.key !== 'k' || !editor) return;
      const { from, to } = editor.state.selection;
      if (from === to) return;
      e.preventDefault();
      document.querySelectorAll('.text-selection-bubble').forEach(el => {
        el.style.opacity = '0';
        setTimeout(() => { el.style.display = 'none'; }, 200);
      });
      setTimeout(() => { aiButtonRef.current?.click(); }, 210);
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [editor]);

  // ── Render ─────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <Box className="editor-loading" sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>{t('loading_contract')}</Typography>
      </Box>
    );
  }

  const isDarkMode = theme.palette.mode === 'dark';

  return (
    <Box className={`editor-container${isDarkMode ? ' dark-mode' : ''}`}>

      {/* ── Toolbar ── */}
      <Box
        className="toolbar"
        sx={{
          display: 'flex',
          alignItems: 'center',
          p: 1,
          borderBottom: `1px solid ${theme.palette.divider}`,
          flexDirection: 'column',
          position: 'sticky',
          top: 0,
          zIndex: 1300,
        }}
      >
        <Box sx={{ width: '100%', display: 'flex', alignItems: 'center' }}>
          {editor && (
            <ToolbarPlugin
              editor={editor}
              socketRef={socketRef}
              contractTitle={contract?.title || 'Untitled'}
              contractWorkspaceId={contract?.workspace_id || null}
              contractFolderId={contract?.folder_id || null}
              contractWorkspaceName={contract?.workspace_name || null}
              contractFolderName={contract?.folder_name || null}
              onContractMoved={props.onContractMoved}
              saveContract={saveContract}
              selectedImageNode={selectedImageNode}
              hasUnsavedChanges={hasUnsavedChanges}
              onAfterUndoRedo={onAfterUndoRedo}
              aiButtonRef={aiButtonRef}
              contractId={contractId}
              pageSettings={pageSettings}
              onPageSettingsChange={(newSettings) => {
                setPageSettings(newSettings);
                // Trigger immediate save after settings change
                if (socketRef.current && editor) {
                  const payload = { content: editor.getHTML(), settings: { page: newSettings } };
                  socketRef.current.emit('save', payload);
                }
              }}
              suggestModeActive={isReviewer ? true : suggestionHook.suggestMode}
              suggestModeLocked={isReviewer}
              onSuggestModeChange={(enabled) => {
                suggestionHook.setSuggestMode(enabled);
                if (enabled && editor) {
                  hideTextSelectionBubbles();
                  const { from, to } = editor.state.selection;
                  if (from !== to) {
                    try {
                      const startCoords = editor.view.coordsAtPos(from);
                      const endCoords = editor.view.coordsAtPos(to);
                      const selectionY = (startCoords.top + endCoords.bottom) / 2;
                      const text = editor.state.doc.textBetween(from, to, ' ');
                      onSuggestClickRef.current?.({ from, to, text, selectionY });
                    } catch (e) {
                      // ignore coord errors
                    }
                  }
                }
              }}
              onSendComment={(data) => {
                if (socketRef.current) {
                  socketRef.current.emit('comment', { ...data, contract_id: contractId });
                }
              }}
            />
          )}
        </Box>

        {/* Active collaborators */}
        {activeUsers.length > 1 && (
          <Box sx={{ width: '100%', display: 'flex', alignItems: 'center' }}>
            <Box className="collaborators" sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
              {activeUsers.map((user) => (
                <Tooltip
                  key={user.id}
                  title={`${user.name}${user.id === currentUser?.id ? ' (you)' : ''}`}
                  placement="top"
                  arrow
                >
                  <Chip
                    size="small"
                    avatar={
                      <Avatar
                        sx={{
                          bgcolor: user.id === currentUser?.id ? 'primary.main' : user.color,
                          fontWeight: 'bold',
                          boxShadow: user.id === currentUser?.id ? `0 0 0 2px ${theme.palette.background.paper}` : 'none',
                        }}
                      >
                        {user.name.charAt(0).toUpperCase()}
                      </Avatar>
                    }
                    label={user.name}
                    variant="filled"
                    sx={{
                      borderRadius: 2,
                      backgroundColor: user.id === currentUser?.id
                        ? alpha(theme.palette.primary.main, 0.12)
                        : theme.palette.action.hover,
                      color: user.id === currentUser?.id ? 'primary.main' : 'text.primary',
                      fontWeight: user.id === currentUser?.id ? 500 : 400,
                      border: `1px solid ${theme.palette.divider}`,
                      '&:hover': {
                        backgroundColor: user.id === currentUser?.id
                          ? alpha(theme.palette.primary.main, 0.18)
                          : theme.palette.action.selected,
                      },
                      transition: 'all 0.2s ease-in-out',
                    }}
                  />
                </Tooltip>
              ))}
            </Box>
          </Box>
        )}
      </Box>

      {/* ── Editor body ── */}
      <Box className={`editor-body${hasCards ? ' has-cards' : ''}`}>
        {/* Content column — cards overlay sits absolutely inside */}
        <Box className="editor-content-column editor-scroll-container">
          {!editor || contentLoading ? (
            <Box sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '85vh',
              backgroundColor: theme.palette.background.paper,
            }}>
              <CircularProgress size={24} sx={{ mr: 1 }} />
              <Typography>{t('document_editor.loading_content')}</Typography>
            </Box>
          ) : (
            <EditorContent
              editor={editor}
              className="tiptap-editor"
              dir={isRTL ? 'rtl' : 'ltr'}
              lang={isRTL ? 'ar' : undefined}
              style={{ visibility: contentLoading ? 'hidden' : 'visible' }}
            />
          )}

          {/* Cards column — absolute overlay in the right margin (Google Docs style) */}
          {hasCards && (
            <Box className="editor-cards-column">
              <CardsColumn
                comments={commentHook.comments}
                pendingComment={commentHook.pendingComment}
                commentPositions={commentPositions}
                activeCommentId={commentHook.activeCommentId}
                onSetActiveComment={commentHook.setActiveCommentId}
                commentHandlers={commentHook}
                suggestions={suggestionHook.pendingSuggestions}
                pendingSuggestion={suggestionHook.pendingSuggestion}
                suggestionPositions={suggestionPositions}
                activeSuggestionId={suggestionHook.activeSuggestionId}
                onSetActiveSuggestion={suggestionHook.setActiveSuggestionId}
                suggestionHandlers={suggestionHook}
                currentUser={currentUser}
              />
            </Box>
          )}
        </Box>
      </Box>

      {/* Review mode chip is now inside the toolbar */}

      {/* Collab status footer */}
      <CollabStatus status={status} activeUsers={activeUsers} editor={editor} t={t} />

      {/* Save notification */}
      <EditorNotification notification={notification} onClose={closeNotification} />

    </Box>
  );
});

DocumentEditor.displayName = 'DocumentEditor';

export default DocumentEditor;
